import React, { useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Search, Loader2, AlertCircle, CheckCircle2, Chrome, Download, Key, Puzzle } from 'lucide-react';
import { downloadCodeApi } from '../lib/api';
import { ChromeExtensionPanel } from '../components/ChromeExtensionPanel';

interface LookupResult {
  code: string;
  code_type: 'normal' | 'flexible';
  original_filename: string;
  download_filename: string;
  creative_type: string | null;
  vertical: string | null;
  editor_name: string | null;
  request_title: string | null;
  generated_at: string;
}

/**
 * Buyer pastes a campaign name (or just a code). We extract any 8-char codes
 * (lowercase alphanumeric) and resolve each via /download-codes/lookup.
 */
export function CampaignLookupPage() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<{ code: string; result: LookupResult | null; error: string | null }[]>([]);

  const extractCodes = (text: string): string[] => {
    const matches = text.toLowerCase().match(/\b[a-z0-9]{8}\b/g) || [];
    return Array.from(new Set(matches));
  };

  const handleLookup = async () => {
    const codes = extractCodes(input);
    if (codes.length === 0) {
      setResults([{ code: '', result: null, error: 'No 8-character codes found in the input' }]);
      return;
    }
    setLoading(true);
    setResults([]);
    const out: typeof results = [];
    for (const code of codes) {
      try {
        const resp = await downloadCodeApi.lookup(code);
        out.push({ code, result: resp.data?.data, error: null });
      } catch (err: any) {
        out.push({ code, result: null, error: err.response?.data?.error || 'Lookup failed' });
      }
    }
    setResults(out);
    setLoading(false);
  };

  return (
    <DashboardLayout title="Campaign Lookup">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="bg-card border border-border rounded-lg p-5 space-y-3">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="e.g. FB_HI_a2347dgb_Q4_Promo  or just a2347dgb"
            className="w-full p-3 text-sm border border-border rounded-lg bg-background text-foreground resize-none outline-none focus:border-primary"
            rows={3}
          />
          <div className="flex justify-end">
            <button
              onClick={handleLookup}
              disabled={loading || input.trim().length === 0}
              className="px-4 py-2 text-sm font-medium rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white inline-flex items-center gap-2"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Lookup
            </button>
          </div>
        </div>

        {/* Chrome Extension Section */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-3">
            <Chrome size={20} className="text-blue-500 flex-shrink-0" />
            <div>
              <h2 className="text-sm font-semibold">Orbit Chrome Extension</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Look up creative codes instantly while inside Meta Ads Manager — no tab switching needed</p>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {/* Install steps */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">How to install</h3>
              <ol className="space-y-3">
                {[
                  {
                    icon: <Download size={14} />,
                    title: 'Download the extension',
                    desc: 'Ask your admin for the Orbit extension package (.zip file). Save it to your desktop.',
                  },
                  {
                    icon: <Puzzle size={14} />,
                    title: 'Load it in Chrome',
                    desc: 'Open Chrome → go to chrome://extensions → enable "Developer mode" (top right) → click "Load unpacked" → select the extracted extension folder.',
                  },
                  {
                    icon: <Key size={14} />,
                    title: 'Pair with your account',
                    desc: 'Click "Generate Pairing Code" below → copy the code → open the Orbit extension in Chrome → paste the code → click Pair. Done.',
                  },
                  {
                    icon: <Search size={14} />,
                    title: 'Look up codes anywhere',
                    desc: 'Click the Orbit extension icon, type or paste an 8-character code (e.g. 77gw1dbt), press Lookup — see the creative details instantly.',
                  },
                ].map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium flex items-center gap-1.5">{step.icon}{step.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Pairing panel */}
            <div className="border-t border-border pt-4">
              <ChromeExtensionPanel />
            </div>
          </div>
        </div>

        {results.length > 0 && (
          <div className="space-y-3">
            {results.map((r, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-4">
                {r.error ? (
                  <div className="flex items-start gap-3 text-sm">
                    <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      {r.code && <p className="font-mono font-bold">{r.code}</p>}
                      <p className="text-muted-foreground">{r.error}</p>
                    </div>
                  </div>
                ) : r.result ? (
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-base">{r.result.code}</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          r.result.code_type === 'flexible'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}>
                          {r.result.code_type === 'flexible' ? 'FLEX / DYNAMIC' : 'NORMAL'}
                        </span>
                      </div>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <dt className="text-muted-foreground">Original file</dt>
                        <dd className="font-mono break-all">{r.result.original_filename}</dd>
                        <dt className="text-muted-foreground">Creative type</dt>
                        <dd>{r.result.creative_type || '—'}</dd>
                        <dt className="text-muted-foreground">Vertical</dt>
                        <dd>{r.result.vertical || '—'}</dd>
                        <dt className="text-muted-foreground">Editor</dt>
                        <dd>{r.result.editor_name || '—'}</dd>
                        <dt className="text-muted-foreground">File request</dt>
                        <dd>{r.result.request_title || '—'}</dd>
                        <dt className="text-muted-foreground">Generated</dt>
                        <dd>{new Date(r.result.generated_at).toLocaleString()}</dd>
                      </dl>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
